/**
 * The one place a scene can start, and the one place it can be stopped.
 *
 * ## The flow
 *
 * `idle → loading → running → cleanup → cooldown → idle`
 *
 * A request takes the lock **before** it loads anything, so two fast triggers
 * cannot both get through while a chunk is in flight. Once the chunk arrives
 * the request is checked again — same person, same page, still switched on,
 * not cancelled — because any of those can have changed while it loaded.
 * Nothing is ever queued: a request that arrives at a busy moment is dropped.
 *
 * ## Stopping
 *
 * One `AbortController` per run. It aborts on every exit path there is:
 * the scene finishing, Escape, the Stop button, the eight-second watchdog,
 * the preference being switched off, a route change, a sign-out, a change of
 * person, organization or workspace, a resize, the tab being hidden, any
 * interaction with the real page, an error inside the scene, and unmount.
 *
 * Interaction cancels but never *consumes*. The listeners are passive and
 * capture-phase: the button a person actually clicked still receives the
 * click and still does its job, exactly once. A decorative animation is
 * never a reason for a work control to miss an input.
 *
 * ## What it refuses
 *
 * Sign-in, the employee portal, the public trust page, the assessor portal
 * and the security area never run a scene, whatever asks.
 */

import { useRouterState } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  COOLDOWN_MS,
  LOAD_TIMEOUT_MS,
  WATCHDOG_MS,
  effect,
  effectsAllowedOn,
  onRoute,
  type SceneProps,
} from "./registry.ts";
import {
  DEFAULT_STATE,
  readState,
  userKey,
  writeState,
  type DiscoveryState,
} from "./discovery.ts";
import type { EffectId, RunContext, TargetHandle } from "./run.ts";
import { OverlayHost } from "./overlay.tsx";

/** At most this many page regions may move in one scene. */
const MAX_TARGETS = 8;

/** The click that starts a scene must not immediately cancel it. */
const INTERACTION_GRACE_MS = 220;

export type Phase = "idle" | "loading" | "running" | "cooldown";

interface ActiveRun {
  readonly id: EffectId;
  readonly Scene: React.ComponentType<SceneProps>;
  readonly ctx: RunContext;
  readonly announce: string;
}

interface EffectContextValue {
  readonly enabled: boolean;
  readonly setEnabled: (next: boolean) => void;
  readonly phase: Phase;
  readonly activeId: EffectId | null;
  readonly discovered: readonly EffectId[];
  readonly reducedMotion: boolean;
  /** True when a trigger may be worked at all. Idle, on, and on its page. */
  readonly ready: boolean;
  readonly request: (id: EffectId, triggerRect: DOMRect | null) => void;
  readonly registerTarget: (key: string, element: HTMLElement | null) => void;
  readonly stop: () => void;
}

const EffectContext = createContext<EffectContextValue | null>(null);

/** The effect system, or a safe inert stand-in outside the provider. */
export function useEffects(): EffectContextValue {
  return useContext(EffectContext) ?? INERT;
}

const INERT: EffectContextValue = {
  enabled: false,
  setEnabled: () => undefined,
  phase: "idle",
  activeId: null,
  discovered: [],
  reducedMotion: false,
  ready: false,
  request: () => undefined,
  registerTarget: () => undefined,
  stop: () => undefined,
};

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * The identity the run belongs to.
 *
 * Passed in rather than read here, so this module does not import the
 * application shell that renders it. A change to any of the three ends
 * whatever is on screen and loads the next person's own record.
 */
export function EffectProvider({
  userId,
  tenantId,
  workspaceKey,
  children,
}: {
  readonly userId: string | null;
  readonly tenantId: string | null;
  readonly workspaceKey: string | null;
  readonly children: ReactNode;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const [state, setState] = useState<DiscoveryState>(DEFAULT_STATE);
  const [phase, setPhase] = useState<Phase>("idle");
  const [active, setActive] = useState<ActiveRun | null>(null);
  const [reduced, setReduced] = useState(prefersReducedMotion);

  // The lock is a ref, not state: two triggers in one tick must not both win.
  const lock = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const cooldownTimer = useRef<number | undefined>(undefined);
  const targets = useRef(new Map<string, HTMLElement>());

  // --- The person's own record ------------------------------------------

  // A change of person loads that person's key and nothing else. The
  // previous person's discoveries leave memory in the same tick.
  useEffect(() => {
    if (userId === null) {
      setState(DEFAULT_STATE);
      return;
    }
    setState(readState(userKey(userId)));
  }, [userId]);

  const persist = useCallback(
    (next: DiscoveryState) => {
      setState(next);
      if (userId !== null) writeState(userKey(userId), next);
    },
    [userId],
  );

  // --- Stopping ----------------------------------------------------------

  const stop = useCallback(() => {
    controller.current?.abort();
    controller.current = null;
    lock.current = false;
    setActive(null);
    // Every ending leads to the same cooldown, so a cancelled scene cannot be
    // restarted straight away either.
    setPhase("cooldown");
    window.clearTimeout(cooldownTimer.current);
    cooldownTimer.current = window.setTimeout(() => setPhase("idle"), COOLDOWN_MS);
  }, []);

  /** A load that failed started nothing, so it returns straight to idle. */
  const abandon = useCallback(() => {
    controller.current?.abort();
    controller.current = null;
    lock.current = false;
    setActive(null);
    setPhase("idle");
  }, []);

  useEffect(
    () => () => {
      controller.current?.abort();
      window.clearTimeout(cooldownTimer.current);
    },
    [],
  );

  // The system's motion preference, watched for changes mid-scene.
  useEffect(() => {
    let media: MediaQueryList;
    try {
      media = window.matchMedia("(prefers-reduced-motion: reduce)");
    } catch {
      return;
    }
    const onChange = (): void => {
      setReduced(media.matches);
      // A moving scene stops the moment the request arrives.
      if (media.matches && controller.current !== null) stop();
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [stop]);

  // Leaving the page, the organization, the workspace, or the account ends
  // whatever is on screen.
  const context = `${pathname}|${userId ?? ""}|${tenantId ?? ""}|${workspaceKey ?? ""}`;
  const lastContext = useRef(context);
  useEffect(() => {
    if (lastContext.current === context) return;
    lastContext.current = context;
    if (controller.current !== null) stop();
  }, [context, stop]);

  // Switching the feature off ends the current scene at once.
  useEffect(() => {
    if (!state.enabled && controller.current !== null) stop();
  }, [state.enabled, stop]);

  // While a scene runs: Escape, any real interaction, a resize, or a hidden
  // tab ends it. Passive and capture-phase, so the page still gets the event.
  useEffect(() => {
    if (active === null) return;
    let armed = false;
    const arm = window.setTimeout(() => {
      armed = true;
    }, INTERACTION_GRACE_MS);

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        stop();
        return;
      }
      if (armed && !event.repeat) stop();
    };
    const onInteract = (): void => {
      if (armed) stop();
    };
    const onHide = (): void => {
      if (document.visibilityState === "hidden") stop();
    };

    window.addEventListener("keydown", onKey, { capture: true });
    window.addEventListener("pointerdown", onInteract, { capture: true, passive: true });
    window.addEventListener("wheel", onInteract, { capture: true, passive: true });
    window.addEventListener("scroll", onInteract, { capture: true, passive: true });
    window.addEventListener("resize", onInteract, { passive: true });
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.clearTimeout(arm);
      window.removeEventListener("keydown", onKey, { capture: true } as EventListenerOptions);
      window.removeEventListener("pointerdown", onInteract, { capture: true } as EventListenerOptions);
      window.removeEventListener("wheel", onInteract, { capture: true } as EventListenerOptions);
      window.removeEventListener("scroll", onInteract, { capture: true } as EventListenerOptions);
      window.removeEventListener("resize", onInteract);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [active, stop]);

  // --- Targets -----------------------------------------------------------

  const registerTarget = useCallback((key: string, element: HTMLElement | null) => {
    if (element === null) targets.current.delete(key);
    else targets.current.set(key, element);
  }, []);

  // --- Starting ----------------------------------------------------------

  const request = useCallback(
    (id: EffectId, triggerRect: DOMRect | null) => {
      if (lock.current) return;
      if (!state.enabled) return;
      if (!effectsAllowedOn(pathname)) return;
      const definition = effect(id);
      if (!onRoute(definition, pathname)) return;

      // The lock is taken before the chunk is asked for.
      lock.current = true;
      setPhase("loading");

      const runController = new AbortController();
      controller.current = runController;
      const startedFor = { userId, pathname };

      const loadTimer = window.setTimeout(() => {
        if (controller.current === runController) abandon();
      }, LOAD_TIMEOUT_MS);

      definition
        .load()
        .then((module) => {
          window.clearTimeout(loadTimer);
          // A chunk that arrives late must not start on somebody else's
          // screen, on another page, or after a cancellation.
          if (controller.current !== runController) return;
          if (runController.signal.aborted) return;
          if (startedFor.userId !== userId) {
            abandon();
            return;
          }
          if (window.location.pathname !== startedFor.pathname) {
            abandon();
            return;
          }

          const visible = [...targets.current.entries()]
            .filter(([, element]) => element.isConnected)
            .slice(0, MAX_TARGETS)
            .map(([key, element]): TargetHandle => ({
              id: key,
              element,
              // Measured once. A scene never re-measures every frame.
              rect: element.getBoundingClientRect(),
            }));

          const ctx: RunContext = {
            effectId: id,
            signal: runController.signal,
            reducedMotion: reduced,
            viewport: { width: window.innerWidth, height: window.innerHeight },
            trigger: triggerRect,
            targets: visible,
            discovered: state.found,
            finish: () => {
              if (controller.current === runController) stop();
            },
          };

          // The watchdog is the last word, whatever the scene believes.
          const watchdog = window.setTimeout(() => {
            if (controller.current === runController) stop();
          }, WATCHDOG_MS);
          runController.signal.addEventListener(
            "abort",
            () => window.clearTimeout(watchdog),
            { once: true },
          );

          // Starting counts as a discovery, including a reduced-motion one.
          if (!state.found.includes(id)) {
            persist({ ...state, found: [...state.found, id] });
          }

          setActive({ id, Scene: module.default, ctx, announce: definition.announce });
          setPhase("running");
        })
        .catch(() => {
          window.clearTimeout(loadTimer);
          // A chunk that will not load started nothing. No cooldown for it.
          if (controller.current === runController) abandon();
        });
    },
    [abandon, pathname, persist, reduced, state, stop, userId],
  );

  const setEnabled = useCallback(
    (next: boolean) => {
      persist({ ...state, enabled: next });
    },
    [persist, state],
  );

  const value = useMemo<EffectContextValue>(
    () => ({
      enabled: state.enabled,
      setEnabled,
      phase,
      activeId: active?.id ?? null,
      discovered: state.found,
      reducedMotion: reduced,
      ready: phase === "idle" && state.enabled && effectsAllowedOn(pathname),
      request,
      registerTarget,
      stop,
    }),
    [active, pathname, phase, reduced, registerTarget, request, setEnabled, state, stop],
  );

  return (
    <EffectContext.Provider value={value}>
      {children}
      {active !== null && (
        <OverlayHost announce={active.announce} onStop={stop}>
          <active.Scene ctx={active.ctx} />
        </OverlayHost>
      )}
    </EffectContext.Provider>
  );
}
